import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router';

import {
  Card,
  CardBody,
  CardContent,
  CardFooter,
  CardTitle,
} from '@/components/deprecated/Card';
import { getTransparentDivisionIcon } from '@/components/deprecated/DivisionIcon';
import { StatusBoxGradient } from '@/components/deprecated/StatusBoxGradient';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MEDIA_URL } from '@/config';
import { convertNumberToDivision } from '@/utils/numberToDivision';

import { fetchCurrentLeagueTeam } from '../api/getCurrentLeagueTeam';

interface UserStatusBoxProps {
  steamid?: string;
  username?: string;
}

const UserStatusBox: React.FC<UserStatusBoxProps> = ({ steamid, username }) => {
  const { data: currentLeagueTeam, isLoading: currentLeagueTeamLoading } =
    useQuery({
      queryKey: ['userStatusBox', steamid],
      queryFn: () => fetchCurrentLeagueTeam(steamid),
      enabled: !!steamid,
    });

  if (currentLeagueTeamLoading) {
    return (
      <Card>
        <div className="bg-block relative grid h-[200px] w-full place-items-center overflow-hidden rounded-t-xl border-b border-border">
          <Skeleton className="h-32 w-32 !rounded-full"></Skeleton>
          <StatusBoxGradient />
        </div>
        <CardBody>
          <CardTitle className="flex items-center gap-x-2">
            <Skeleton className="h-8 w-8 !rounded-full" />
            <Skeleton className="h-4 w-44" />
          </CardTitle>
          <CardContent>
            <Skeleton className="h-2.5 w-72" />
          </CardContent>
        </CardBody>
        <CardFooter>
          <Skeleton className="h-2.5 w-36" />
        </CardFooter>
      </Card>
    );
  }

  return (
    <>
      {currentLeagueTeam ? (
        <Card>
          <div
            className="bg-block relative grid h-[200px] w-full place-items-center overflow-hidden rounded-t-xl border-b border-border"
            title={
              convertNumberToDivision(currentLeagueTeam.division) + ' Division'
            }
          >
            {getTransparentDivisionIcon(currentLeagueTeam.division, '120')}
            <StatusBoxGradient />
          </div>
          <CardBody>
            <CardTitle className="flex items-center gap-x-2">
              <Avatar>
                <AvatarImage
                  src={`${MEDIA_URL}/images/uploads/${currentLeagueTeam.team.team_photo}`}
                  alt={currentLeagueTeam.team.team_name || ''}
                />
              </Avatar>
              {currentLeagueTeam.team.team_name}
            </CardTitle>
            <CardContent>
              Currently on active roster for Season {currentLeagueTeam.season}
            </CardContent>
          </CardBody>
          <CardFooter>
            <Link to={`/team/${currentLeagueTeam.team.id}`}>
              <Button variant="link">
                Go to team
                <ChevronRight />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ) : (
        <div className="bg-block relative w-full overflow-hidden rounded-xl border border-border p-5 text-sm font-medium text-muted-foreground">
          {username} is not currently on any active roster.
          <StatusBoxGradient />
        </div>
      )}
    </>
  );
};

export default UserStatusBox;
